import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import DatePicker from './DatePicker';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Calendar as CalendarIcon, Ban, Lock, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const dateLocaleMap = { fr: 'fr-FR', en: 'en-US', jp: 'ja-JP' };

export default function AvailabilityCalendar() {
  const { t, i18n } = useTranslation();
  const dateLocale = dateLocaleMap[i18n.language] || 'en-US';

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [unavailable, setUnavailable] = useState([]);
  const [booked, setBooked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAvailability();
  }, []);

  const fetchAvailability = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/guides/me/availability`, { withCredentials: true });
      setUnavailable(response.data.unavailable_dates || []);
      setBooked(response.data.booked_dates || []);
    } catch (error) {
      console.error('Error fetching availability:', error);
    } finally {
      setLoading(false);
    }
  };

  const blockDates = async () => {
    if (!startDate) { toast.error(t('availability.selectStartDate')); return; }
    const finalEnd = endDate || startDate;
    if (new Date(startDate) > new Date(finalEnd)) { toast.error(t('availability.endBeforeStart')); return; }

    setSaving(true);
    try {
      const dates = [];
      let current = new Date(startDate);
      const end = new Date(finalEnd);
      while (current <= end) {
        dates.push(current.toISOString().split('T')[0]);
        current.setDate(current.getDate() + 1);
      }
      const res = await axios.post(`${API_URL}/api/guides/me/availability`, { dates }, { withCredentials: true });
      setStartDate('');
      setEndDate('');
      await fetchAvailability();
      toast.success(t('availability.blockedSuccess', { count: (res.data.added || dates).length }));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('availability.error'));
    } finally {
      setSaving(false);
    }
  };

  const unblockDate = async (date) => {
    setSaving(true);
    try {
      await axios.delete(`${API_URL}/api/guides/me/availability/${date}`, { withCredentials: true });
      setUnavailable(unavailable.filter((d) => d !== date));
      toast.success(t('availability.unblockedSuccess'));
    } catch (error) {
      toast.error(t('availability.error'));
    } finally {
      setSaving(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];

  const groupByMonth = (dates) =>
    dates.reduce((acc, date) => {
      const monthKey = date.substring(0, 7);
      if (!acc[monthKey]) acc[monthKey] = [];
      acc[monthKey].push(date);
      return acc;
    }, {});

  const formatMonth = (monthKey) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(year, parseInt(month, 10) - 1);
    return date.toLocaleDateString(dateLocale, { month: 'long', year: 'numeric' });
  };

  const formatDay = (date) =>
    new Date(date + 'T00:00:00').toLocaleDateString(dateLocale, { weekday: 'long', day: 'numeric', month: 'long' });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 text-[#F9004D] animate-spin" />
        <span className="ml-3 text-zinc-400">{t('availability.loading')}</span>
      </div>
    );
  }

  const blockedByMonth = groupByMonth(unavailable);
  const bookedByMonth = groupByMonth(booked);

  return (
    <div className="space-y-6" data-testid="availability-calendar">
      {/* Intro */}
      <div className="bg-zinc-800/30 border border-[#00F0FF]/30 rounded-lg p-4">
        <p className="text-sm text-zinc-300">{t('availability.intro')}</p>
        <p className="text-xs text-zinc-500 mt-2">
          <strong className="text-[#00F0FF]">💡</strong> {t('availability.tipText')}
        </p>
      </div>

      {/* Block dates form */}
      <Card className="bg-zinc-900/50 border-white/10 p-6">
        <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
          <Ban className="h-5 w-5 text-[#F9004D]" /> {t('availability.blockRangeTitle')}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <Label className="text-zinc-300 mb-2 block">{t('availability.startDate')}</Label>
            <DatePicker
              value={startDate}
              onChange={setStartDate}
              minDate={today}
              testId="block-start-date"
            />
          </div>
          <div>
            <Label className="text-zinc-300 mb-2 block">{t('availability.endDate')}</Label>
            <DatePicker
              value={endDate}
              onChange={setEndDate}
              minDate={startDate || today}
              testId="block-end-date"
            />
          </div>
        </div>

        <Button
          onClick={blockDates}
          disabled={saving || !startDate}
          className="w-full bg-[#F9004D] hover:bg-[#D00040] text-white py-3"
          data-testid="block-dates-btn"
        >
          {saving ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Ban className="h-5 w-5 mr-2" />}
          {t('availability.blockBtn')}
        </Button>
      </Card>

      {/* Blocked dates list */}
      <Card className="bg-zinc-900/50 border-white/10 p-6">
        <h3 className="text-xl font-bold text-white mb-4">
          {t('availability.blockedTitle')} ({unavailable.length})
        </h3>

        {unavailable.length === 0 ? (
          <div className="text-center py-10">
            <CalendarIcon className="h-14 w-14 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-400">{t('availability.none')}</p>
            <p className="text-zinc-500 text-sm mt-2">{t('availability.noneDesc')}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.keys(blockedByMonth).sort().map((monthKey) => (
              <div key={monthKey}>
                <h4 className="text-lg font-semibold text-[#F9004D] mb-3 capitalize">{formatMonth(monthKey)}</h4>
                <div className="space-y-2">
                  {blockedByMonth[monthKey].sort().map((date) => (
                    <div
                      key={date}
                      className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/50 border border-white/10"
                      data-testid={`blocked-${date}`}
                    >
                      <div className="flex items-center gap-3">
                        <Ban className="h-5 w-5 text-[#F9004D]" />
                        <span className="text-white font-medium capitalize">{formatDay(date)}</span>
                      </div>
                      <button
                        onClick={() => unblockDate(date)}
                        disabled={saving}
                        className="text-zinc-400 hover:text-[#00F0FF] transition-colors disabled:opacity-50 text-sm flex items-center gap-1"
                        title={t('availability.unblock')}
                      >
                        <Trash2 className="h-4 w-4" /> {t('availability.unblock')}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Booked dates (read-only) */}
      {booked.length > 0 && (
        <Card className="bg-zinc-900/50 border-white/10 p-6">
          <h3 className="text-xl font-bold text-white mb-1 flex items-center gap-2">
            <Lock className="h-5 w-5 text-[#10B981]" /> {t('availability.bookedTitle')} ({booked.length})
          </h3>
          <p className="text-zinc-500 text-sm mb-4">{t('availability.bookedDesc')}</p>
          <div className="space-y-6">
            {Object.keys(bookedByMonth).sort().map((monthKey) => (
              <div key={monthKey}>
                <h4 className="text-lg font-semibold text-[#10B981] mb-3 capitalize">{formatMonth(monthKey)}</h4>
                <div className="space-y-2">
                  {bookedByMonth[monthKey].sort().map((date) => (
                    <div
                      key={date}
                      className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/30 border border-white/5"
                      data-testid={`booked-${date}`}
                    >
                      <Lock className="h-5 w-5 text-[#10B981]" />
                      <span className="text-zinc-300 font-medium capitalize">{formatDay(date)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
